/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2021 Adobe
 *  All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

import React, {FC, useEffect, useState, useRef} from 'react';
import {injectIntl} from 'react-intl';
import {connect, useDispatch} from 'react-redux';
import {fetchMLAppRanking} from '../../actions/predictActions';
import AppHomeTab from '../../components/AppHomeTab/AppHomeTab';
import AppHomeContent from '../../components/AppHomeTab/AppHomeContent';
import RecentWork from '../../components/RecentWork/RecentWork';
import UIStore from '../../redux/UIStore';
import './AppHome.scss';
import {CREATE_NOTIFICATION, SET_CCPROJECTS_FINISHED, SET_CCPROJECTS_TIMEOUT_OR_ERROR, REMOVE_NOTIFICATION} from '../../types';
import {createNotificationPayload, removeNotificationPayload} from '../../components/Notification/Notification';
import appHomeData from './appHomeData';
import ModalContainer from '@react/react-spectrum/ModalContainer';
import IllustratedMessage from '@react/react-spectrum/IllustratedMessage';
import Dialog from '@react/react-spectrum/Dialog';
import CCProjectsClientService from '../../services/CCProjectsClientService';
import Wait from '@react/react-spectrum/Wait';
import supportedBrowsersData from './appHomeSupportedBrowsers.json';
import AppHomeAnalytics from '../../../utils/AppHomeAnalytics';
import fetchAppHomeData from '../../hooks/fetchAppHomeData';
import fetchAppHomeBannerData from '../../hooks/fetchAppHomeBannerData';
import {getLocalizedURL} from '@intl/adobe-locales';
import AppHomeCapabilityService from '../../services/AppHomeCapabilityService';

type AppHomeProps = {
	locale: string;
	env: string;
	route: {
		routes: any[];
		path: string;
	};
	profileFetchFinished: boolean;
	customerSegment: string;
	storageQuota: number;
	fetchMLAppRanking: () => void;
	checkRecentDataStatus: symbol;
	planCode: string;
	intl: any;
	history: any;
	language: string;
	location: Object;
	leftRailOpen: boolean;
	setLeftRail: (newstate: boolean) => any;
	notifications: Array<any>;
};

const AppHome: FC<AppHomeProps> = (props) => {

	// Ref for timing duration of initial render
	const initialRender = useRef(true);
	const startLoadingTime = performance.now();

	const appHomeAnalytics = new AppHomeAnalytics();

	const {locale, env, route, fetchMLAppRanking, history, intl, notifications} = props;
	const [notificationStyle, setNotificationStyle] = useState({});
	const userEntitled = useRef(false); // remove this after we are out of betas
	const dispatch = useDispatch();

	const sapCode = getSapCodeFromPath(route.path);

	const features = window.cchome.util.getAllFeatures();
	if (
		(sapCode === 'ILST')
		&& (!features.includes('apphomes_illustrator'))
		&& (features.includes('ai_signup'))
		&& (!window.cchome.util.isIE())
	) {
		// Remove later: If user is not part of ai floodgate private beta campaign, redirect to sign up
		window.open('https://pages.adobe.com/creativecloud/en/illustrator-on-the-web/private-beta', '_blank');
		history.push('/cc');
	}
	if (
		(!window.cchome.util.hasFeature('apphomes')
		|| (sapCode === 'FRED' && !features.includes('apphomes_canvas'))
		|| (sapCode === 'PHSP' && !features.includes('apphomes_photoshop_web'))
		|| (sapCode === 'ILST' && !features.includes('apphomes_illustrator')))
		|| (window.cchome.util.isIE())
	) {
		history.push('/cc');
	}
	else {
		userEntitled.current = true;
	}

	if (features.includes('enable_logger')) {
		console.time('recent_new_cached');
		console.time('recent_new_uncached_start');
		console.time('recent_new_uncached_end');
	}

	const [showRecentsOnTop, setShowRecentsOnTop] = useState(true);
	const [ccProjectsClient, setCCProjectsClient] = useState<any | null>(null);

	useEffect(() => {
		if (userEntitled.current) {
			determineIfBrowserSupported(sapCode);
			getAppHomeContainerStyle();
		}
	}, []);

	useEffect(() => {
		window.addEventListener('breakpointChanged', getAppHomeContainerStyle);
		return () => {
			window.removeEventListener('breakpointChanged', getAppHomeContainerStyle);
		};
	}, []);

	// When want to send a dispatch to remove unsupported toast if we navigate away
	useEffect(() => () => {
		dispatch({type: REMOVE_NOTIFICATION, payload: removeNotificationPayload(`${sapCode}-unsupported`)});
	}, [dispatch, sapCode]);

	useEffect(() => {
		getAppHomeContainerStyle();
	}, [notifications]);

	useEffect(() => {
		initializeProjects();
	}, []);

	const initializeProjects = async() => {
		try {
			const projectsClient = await new CCProjectsClientService().getCCProjectsClient();
			setCCProjectsClient(projectsClient);
			dispatch({type: SET_CCPROJECTS_FINISHED});
		}
		catch (e) {
			dispatch({type: SET_CCPROJECTS_TIMEOUT_OR_ERROR});
		}
	};

	// custom favicon for home pages
	useEffect(() => {

		const favicon = document.getElementById('site-favicon') as HTMLLinkElement;

		const updateFavicon = favicon !== null && features.includes('apphomes_favicon');

		if (updateFavicon) {
			switch (sapCode) {
				case 'ILST':
					favicon.href = '/assets/common/ai_beta_appicons_256.svg';
					break;
				case 'PHSP':
					favicon.href = '/assets/common/ps_beta_appicons_256.svg';
					break;
				case 'FRED':
				default:
					favicon.href = '/cc_favicon_48.png';
					break;
			}
		}

		return () => {
			if (updateFavicon) {
				favicon.href = '/cc_favicon_48.png';
			}
		};
	}, [features, sapCode]);

	const getAppHomeContainerStyle = () => {
		// Adjust the height of the content on the page as notifications are meant to be fixed, but items need to be pushed down in relative fashion
		const style: any = {};
		const breakpoint = window.cchome.util.currentBreakpoint;
		let breakpointMod = 0;
		switch (breakpoint) {
			case 'SS':
				breakpointMod = 32;
				break;
			case 'SX':
				breakpointMod = 48;
				break;
		}
		if (document.getElementsByClassName('notification-module-adjustment')[0]) {
			const top = document.getElementsByClassName('notification-module-adjustment')[0].clientHeight + breakpointMod;
			style.marginTop = `${top}px`;
		}

		setNotificationStyle(style);
	};

	const determineIfBrowserSupported = async(sapCode) => {
		// In the future we will call the apps for their browser capabilities here at some endpoint
		let supportedBrowsers = supportedBrowsersData[sapCode].browsers;
		if (window.cchome.util.hasFeature('psw_capabilities') && sapCode === 'PHSP') {
			try {
				const capabilitiesResponse = await new AppHomeCapabilityService().getSupportedBrowsers(sapCode);
				supportedBrowsers = capabilitiesResponse;
			}
			catch (error) {
				window.cchome.util.logger.log(
					'AppHome',
					'apphome capabilities: apphome failed to get capabilities.json',
					error,
				);
				supportedBrowsers = supportedBrowsersData[sapCode].browsers;
			}
		}

		import(/* webpackChunkName: "browserListUserAgent" */ 'browserslist-useragent-regexp').then((lib) => {
			const regExp = lib.getUserAgentRegExp({browsers: supportedBrowsers, allowHigherVersions: true});
			const supported = regExp.test(navigator.userAgent);
			if (!supported || (window.cchome.util.isMobileOrTabletBrowser())) {
				// If we can't detect the current browser, it is unsupported, or a mobile or tablet browser, we create an unsupported notification
				const message = intl.formatMessage({id: `appHomes.unsupported.${sapCode}`});
				if (sapCode === 'PHSP' && features.includes('psw_learnmore')) {
					dispatch({
						type: CREATE_NOTIFICATION,
						payload: createNotificationPayload(`${sapCode}-unsupported`, message, {
							actionLabel: intl.formatMessage({id: 'app.learnmore'}),
							onActionCallback: () => window.open(getLocalizedURL('https://helpx.adobe.com/photoshop/system-requirements-web.html', props.locale), '_blank'),
						}),
					});
				}
				else {
					dispatch({type: CREATE_NOTIFICATION, payload: createNotificationPayload(`${sapCode}-unsupported`, message, {})});
				}
			}
		});
	};

	const createSpaces = async() => {
		const canvasEntitled = await new CCProjectsClientService().userHasCanvasEntitlement();
		if (!canvasEntitled) {
			const errorTitle = props.intl.formatMessage({id: 'appHomes.error.modal.canvas.capability.title'});
			const errorDesc = props.intl.formatMessage({id: 'appHomes.error.modal.canvas.capability.description'});
			const errorModal = (
				<Dialog confirmDisabled={false} confirmLabel={props.intl.formatMessage({id: 'apphomes.modal.ok'})} open role='dialog' title={errorTitle} trapFocus variant='error' id='canvas-error-dialog'>
					<span>{errorDesc}</span>
				</Dialog>
			);
			ModalContainer.show(errorModal, this);
		}
		else {
			try {
				await new CCProjectsClientService().createCanvas(intl.formatMessage({id: 'appHomes.files.newCanvas.newSpace'}));
			}
			catch (e) {
				const errorTitle = props.intl.formatMessage({id: 'appHomes.error.modal.canvas.title'});
				const errorDesc = props.intl.formatMessage({id: 'appHomes.error.modal.canvas.description'});
				const errorModal = (
					<Dialog confirmDisabled={false} confirmLabel={props.intl.formatMessage({id: 'apphomes.modal.ok'})} open role='dialog' title={errorTitle} trapFocus variant='error' id='canvas-error-dialog'>
						<span>{errorDesc}</span>
					</Dialog>
				);
				ModalContainer.show(errorModal, this);
			}
		}
	};

	useEffect(() => {
		if (features.includes('20190412_Home_ML')) {
			fetchMLAppRanking();
		}
	}, [features]);

	if (!features.length) {return null;}

	const handleRouteChange = (route: string) => {
		history.push(route);
	};

	const shiftRecentsToBottom = () => {
		setShowRecentsOnTop(false);
	};

	const hasLeftRail = features.includes('apphomes');

	const isOpen = hasLeftRail && props.leftRailOpen && document.body.clientWidth >= 960;

	const recent = (
		<RecentWork
			key='recent-container'
			sapcode={sapCode}
			env={env}
			shiftRecentsToBottom={shiftRecentsToBottom}
			locale={locale}
			isleftNavOpen={isOpen}
			createSpacesCallback={() => createSpaces()}
		/>
	);

	// Fetch the data to load App Homes.
	let appContextType;
	switch (sapCode) {
		case 'FRED':
			appContextType = 'canvas';
			break;
		case 'PHSP':
			appContextType = 'photoshopweb';
			break;
		case 'ILST':
			appContextType = 'illustratorweb';
			break;
		default:
			appContextType = 'canvas';
	}

	const appHomesState = fetchAppHomeData(appContextType);
	const appHomesBannerState = fetchAppHomeBannerData(appContextType, locale);

	let appHome = null;
	if (window.cchome.util.hasFeature('apphomes_ucs_data')) {
		if (appHomesState.status === 'pending') {
			appHome = (
				<div className={`app-home-container${isOpen ? ' leftnav-open loading' : ' loading'}`} style={notificationStyle}>
					<Wait centered size='L' />
				</div>
			);
		}
		else if (appHomesState.status === 'rejected') {
			const errorHeading = intl.formatMessage({id: 'app.errorpage.generic.title'});
			const errorDesc = intl.formatMessage({id: 'app.errorpage.generic.detail'});
			appHome = (
				<div className={`app-home-container${isOpen ? ' leftnav-open loading' : ' loading'}`} style={notificationStyle}>
					<IllustratedMessage
						description={errorDesc}
						heading={errorHeading}
						illustration={
							<svg height='103' viewBox='0 0 150 103' width='150'>
								<path d='M133.7,8.5h-118c-1.9,0-3.5,1.6-3.5,3.5v27c0,0.8,0.7,1.5,1.5,1.5s1.5-0.7,1.5-1.5V23.5h119V92c0,0.3-0.2,0.5-0.5,0.5h-118c-0.3,0-0.5-0.2-0.5-0.5V69c0-0.8-0.7-1.5-1.5-1.5s-1.5,0.7-1.5,1.5v23c0,1.9,1.6,3.5,3.5,3.5h118c1.9,0,3.5-1.6,3.5-3.5V12C137.2,10.1,135.6,8.5,133.7,8.5z M15.2,21.5V12c0-0.3,0.2-0.5,0.5-0.5h118c0.3,0,0.5,0.2,0.5,0.5v9.5H15.2z M32.6,16.5c0,0.6-0.4,1-1,1h-10c-0.6,0-1-0.4-1-1s0.4-1,1-1h10C32.2,15.5,32.6,15.9,32.6,16.5z M13.6,56.1l-8.6,8.5C4.8,65,4.4,65.1,4,65.1c-0.4,0-0.8-0.1-1.1-0.4c-0.6-0.6-0.6-1.5,0-2.1l8.6-8.5l-8.6-8.5c-0.6-0.6-0.6-1.5,0-2.1c0.6-0.6,1.5-0.6,2.1,0l8.6,8.5l8.6-8.5c0.6-0.6,1.5-0.6,2.1,0c0.6,0.6,0.6,1.5,0,2.1L15.8,54l8.6,8.5c0.6,0.6,0.6,1.5,0,2.1c-0.3,0.3-0.7,0.4-1.1,0.4c-0.4,0-0.8-0.1-1.1-0.4L13.6,56.1z' />
							</svg>
						}
					/>
				</div>
			);
		}
		else if (appHomesState.status === 'fulfilled') {
			let bannerData;
			if (appHomesBannerState.status === 'pending') {
				bannerData = null;
			}
			else if (appHomesBannerState.status === 'rejected') {
				bannerData = null;
			}
			else if (appHomesBannerState.status === 'fulfilled') {
				bannerData = appHomesBannerState.value;
			}

			appHome = (
				<div className={`app-home-container${isOpen ? ' leftnav-open' : ''}`} style={notificationStyle}>
					<AppHomeContent
						bannerData={bannerData}
						ucsAppHomeData={appHomesState.value}
						leftRailOpen={isOpen}
						recent={recent}
						intl={intl}
						createSpacesCallback={() => createSpaces()}
					/>
				</div>
			);
		}
	}
	else {
		appHome = (
			<div className={`app-home-container${isOpen ? ' leftnav-open' : ''}`} style={notificationStyle}>
				{/* <Route path="/webapps">
						<AppHomeTab appHomeData={appHomeData[sapCode]} leftRailOpen={isOpen} recent={recent} />
					</Route> */}
				<AppHomeTab
					appHomeData={appHomeData[sapCode]}
					leftRailOpen={isOpen}
					recent={recent}
					intl={intl}
					createSpacesCallback={() => createSpaces()}
				/>
			</div>
		);
	}

	if (initialRender.current === true) {
		// Post successful first render of apphome and duration of render
		appHomeAnalytics.postAppHomeRenderSuccess(true, performance.now() - startLoadingTime);
		initialRender.current = false;
		appHomeAnalytics.postAppHomeRender();
	}

	return appHome;
};

const getSapCodeFromPath = (path: string) => {
	const name = path.split('/')[2];
	switch (name) {
		case 'canvas':
			return 'FRED';
		case 'photoshop':
			return 'PHSP';
		case 'illustrator':
			return 'ILST';
	}
	throw new Error('path does not include app name.');
};

const mapStateToProps = state => ({
	customerSegment: state.profile.subscriptionInfo.customerSegment,
	storageQuota: state.profile.storage.storage_quota,
	predictAppRanking: state.predict.predictAppRanking,
	profileFetchFinished: state.profile.profileFetchFinished,
	planCode: state.profile.subscriptionInfo.planCode,
	language: state.app.language,
	leftRailOpen: state.uistore.leftRailOpen,
	notifications: state.appNotifications.notifications,
});

const mapDispatchToProps = {
	fetchMLAppRanking,
	setLeftRail: UIStore.setLeftRail,
};

export default {
	component: connect(mapStateToProps, mapDispatchToProps)(injectIntl(AppHome)),
};
export type {AppHomeProps};
